import * as sodium from 'libsodium-wrappers-sumo';
import { getKeyManager } from './keys';

// Encryption utilities for the local-first budget app
export class CryptoBox {
  private static instance: CryptoBox;
  private isInitialized = false;

  private constructor() {}

  static getInstance(): CryptoBox {
    if (!CryptoBox.instance) {
      CryptoBox.instance = new CryptoBox();
    }
    return CryptoBox.instance;
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    await sodium.ready;
    this.isInitialized = true;
  }

  // Encrypt data using the current DEK
  async encrypt(data: Uint8Array, metadata?: Uint8Array): Promise<Uint8Array> {
    this.ensureInitialized();
    
    const keyManager = await getKeyManager();
    const dek = keyManager.getDataEncryptionKey();
    
    if (!dek) {
      throw new Error('Application is locked. Cannot encrypt data.');
    }

    const nonce = sodium.randombytes_buf(24); // XChaCha20-Poly1305 nonce
    
    const encrypted = sodium.crypto_aead_xchacha20poly1305_ietf_encrypt(
      data,
      metadata || null, // Additional authenticated data (optional)
      null, // No secret nonce
      nonce,
      dek
    );

    // Return nonce + ciphertext
    const result = new Uint8Array(nonce.length + encrypted.length);
    result.set(nonce);
    result.set(encrypted, nonce.length);
    
    return result;
  }

  // Decrypt data using the current DEK
  async decrypt(encryptedData: Uint8Array, metadata?: Uint8Array): Promise<Uint8Array> {
    this.ensureInitialized();
    
    const keyManager = await getKeyManager();
    const dek = keyManager.getDataEncryptionKey();
    
    if (!dek) {
      throw new Error('Application is locked. Cannot decrypt data.');
    }

    if (encryptedData.length < 24) {
      throw new Error('Invalid encrypted data: too short');
    }

    try {
      const nonce = encryptedData.slice(0, 24);
      const ciphertext = encryptedData.slice(24);
      
      const decrypted = sodium.crypto_aead_xchacha20poly1305_ietf_decrypt(
        null, // No secret nonce
        ciphertext,
        metadata || null, // Additional authenticated data
        nonce,
        dek
      );

      return decrypted;
    } catch (error) {
      throw new Error(`Failed to decrypt data: ${error}`);
    }
  }

  // Encrypt a string and return base64
  async encryptString(text: string, metadata?: string): Promise<string> {
    const data = new TextEncoder().encode(text);
    const metadataBytes = metadata ? new TextEncoder().encode(metadata) : undefined;
    const encrypted = await this.encrypt(data, metadataBytes);
    return sodium.to_base64(encrypted);
  }

  // Decrypt from base64 and return string
  async decryptString(encryptedBase64: string, metadata?: string): Promise<string> {
    const encrypted = sodium.from_base64(encryptedBase64);
    const metadataBytes = metadata ? new TextEncoder().encode(metadata) : undefined;
    const decrypted = await this.decrypt(encrypted, metadataBytes);
    return new TextDecoder().decode(decrypted);
  }

  // Encrypt JSON object
  async encryptJSON(obj: any, metadata?: string): Promise<string> {
    const json = JSON.stringify(obj);
    return await this.encryptString(json, metadata);
  }

  // Decrypt to JSON object
  async decryptJSON<T>(encryptedBase64: string, metadata?: string): Promise<T> {
    const json = await this.decryptString(encryptedBase64, metadata);
    return JSON.parse(json) as T;
  }

  // Create a hash of data (for blob deduplication)
  hash(data: Uint8Array): string {
    this.ensureInitialized();
    const hash = sodium.crypto_generichash(32, data);
    return sodium.to_hex(hash);
  }

  // Hash a string
  hashString(text: string): string {
    const data = new TextEncoder().encode(text);
    return this.hash(data);
  }

  private ensureInitialized(): void {
    if (!this.isInitialized) {
      throw new Error('CryptoBox not initialized. Call initialize() first.');
    }
  }
}

// Singleton instance
let cryptoBox: CryptoBox | null = null;

export const getCryptoBox = async (): Promise<CryptoBox> => {
  if (!cryptoBox) {
    cryptoBox = CryptoBox.getInstance();
    await cryptoBox.initialize();
  }
  return cryptoBox;
};